import React, { useState, useEffect } from 'react';
import { XCircle, CheckCircle, Image as ImageIcon, Film, Tag, Play } from 'lucide-react';
import fashionScenes from './data/fashionScenes.json';

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=400&q=80',
  'https://images.unsplash.com/photo-1465101046530-73398c7f28ca?auto=format&fit=crop&w=400&q=80',
  'https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=400&q=80',
];

const TemplateSelector = ({ open, onClose, selectedTemplate, onSelect, productDetails, targetAudience, tone }) => {
  const [activeTab, setActiveTab] = useState('women-fashion');
  const [selectedGarmentType, setSelectedGarmentType] = useState('all');

  const garmentTypes = {
    all: 'All Garments',
    lehenga: 'Lehenga',
    saree: 'Saree',
    suit: 'Suit',
    anarkali: 'Anarkali',
    salwar: 'Salwar Kameez',
    gown: 'Gown',
    kurta: 'Kurta'
  };

  const mensGarmentTypes = {
    all: 'All Garments',
    kurta: 'Kurta',
    'kurta-dhoti': 'Kurta Dhoti',
    suit: 'Suit',
    trouser: 'Trouser',
    blazer: 'Blazer',
    jacket: 'Jacket',
    'nehru jacket': 'Nehru Jacket',
    sherwani: 'Sherwani',
    waistcoat: 'Waistcoat',
    shirt: 'Shirt'
  };

  // New filtering logic for Women Fashion tab
  const womenFashionTemplates = [
    {
      id: "lehenga_001",
      name: "Lehenga Studio Shoot",
      description: "Cinematic video shoot of a beautiful Indian model wearing a lehenga in a modern fashion studio",
      prompt: "A cinematic video shoot of a beautiful Indian model wearing the same lehenga as shown in the reference images. The shoot is set in a modern fashion studio with soft ambient lighting, large studio lights, neutral backdrops, and subtle props like clothing racks or fabric rolls in the background to add a professional designer studio vibe. Use low camera angles to highlight the grandeur and flow of the flared skirt as the model gracefully walks, twirls, and poses. The camera should slowly pan from the hem of the lehenga upwards, capturing the intricate embroidery on the skirt, blouse, and dupatta. Include slow-motion shots of the dupatta flowing naturally for added drama. Add soft lens flares, shallow depth of field, and subtle light reflections for a rich, film-like feel that enhances the studio atmosphere. Realistic posing guidelines: ✅ The model's neck should remain naturally aligned — no unnatural twisting or bending beyond normal range. ✅ Shoulders, arms, and wrists should bend naturally — no abnormal or exaggerated twists of hands or arms. ✅ Fingers and hands should appear relaxed, elegant, and naturally positioned. ✅ Overall body posture should be balanced and comfortable, maintaining realistic human proportions. ✅ Facial expressions should be calm and poised, matching the graceful, luxurious vibe of a high-end fashion studio shoot",
      category: "lehenga",
      garmentType: "lehenga",
      tags: ["lehenga", "indian", "cinematic", "studio", "silver-embroidery", "navy-blue", "slow-motion"],
      illustrativeImage: "https://i.ibb.co/example/lehenga-studio.jpg",
      videoSample: "/pose1.mp4"
    },
    {
      id: "lehenga_002",
      name: "Lehenga Hyper-Realistic",
      description: "Hyper-realistic cinematic video shoot of a beautiful Indian model wearing a lehenga in a modern fashion studio",
      prompt: "A hyper-realistic cinematic video shoot of a beautiful Indian model wearing the lehenga as shown in the reference images. The shoot is set in a modern fashion studio with soft ambient lighting, large studio lights, neutral backdrops, and subtle props such as clothing racks or fabric rolls in the background to create an authentic designer studio atmosphere. Use low camera angles to emphasize the grandeur and natural flow of the flared skirt as the model gracefully walks, twirls, and poses. The camera should slowly pan from the hem of the lehenga upwards, highlighting the intricate embroidery on the skirt, blouse, and dupatta. Include slow-motion shots that capture the movement of the fabric, especially the dupatta flowing naturally in the air, to add dramatic cinematic impact. Use subtle lens flares, shallow depth of field, and gentle light reflections to enhance the rich, film-like mood of the shoot. Realistic posing and outfit guidelines: The model's blouse design must exactly match the style, cut, and embroidery details of the blouse shown in the reference images. The model's tummy should appear normal, with no artificial flattening or exaggeration. No belt or accessory should be added around the belly area; keep the look authentic to the original lehenga set. The model's neck should remain naturally aligned, with no unnatural twisting or bending beyond a normal human range. Shoulders, arms, and wrists should bend naturally, with no abnormal or exaggerated twists of hands or arms. Fingers and hands should appear relaxed, elegant, and naturally positioned. Overall body posture should be balanced and comfortable, maintaining realistic and proportionate human anatomy. Facial expressions should be calm, poised, and graceful, matching the luxurious vibe of a high-end fashion studio shoot. Ensure that a real, realistic human model is depicted wearing the lehenga in the video. No AI distortions, unreal or doll-like figures, or unrealistic body features should appear in any shot. This video should deliver a hyper-realistic, immersive, and elegant look, capturing the true craftsmanship of the lehenga collection while keeping all visuals natural and authentic.",
      category: "lehenga",
      garmentType: "lehenga",
      tags: ["lehenga", "indian", "hyper-realistic", "cinematic", "studio", "authentic", "slow-motion"],
      illustrativeImage: "https://i.ibb.co/example/lehenga-hyper-realistic.jpg",
      videoSample: "/pose2.mp4"
    },
    {
      id: "lehenga_003",
      name: "Lehenga Authentic Studio",
      description: "Hyper-realistic cinematic video shoot of a beautiful Indian model wearing a lehenga in a modern fashion studio",
      prompt: "A hyper-realistic cinematic video shoot of a beautiful Indian model wearing the lehenga as shown in the reference images. The shoot is set in a modern fashion studio with soft ambient lighting, large studio lights, neutral backdrops, and subtle props such as clothing racks or fabric rolls in the background to create an authentic designer studio atmosphere. Use low camera angles to emphasize the grandeur and natural flow of the flared skirt as the model gracefully walks, twirls, and poses. The camera should slowly pan from the hem of the lehenga upwards, highlighting the intricate embroidery on the skirt, blouse, and dupatta. Include slow-motion shots that capture the movement of the fabric, especially the dupatta flowing naturally in the air, to add dramatic cinematic impact. Use subtle lens flares, shallow depth of field, and gentle light reflections to enhance the rich, film-like mood of the shoot. Realistic posing and outfit guidelines: The model's blouse design must exactly match the style, cut, and embroidery details of the blouse shown in the reference images. The model's tummy should appear normal, with no artificial flattening or exaggeration. No belt or accessory should be added around the belly area; keep the look authentic to the original lehenga set. The model's neck should remain naturally aligned, with no unnatural twisting or bending beyond a normal human range. Shoulders, arms, and wrists should bend naturally, with no abnormal or exaggerated twists of hands or arms. Fingers and hands should appear relaxed, elegant, and naturally positioned. Overall body posture should be balanced and comfortable, maintaining realistic and proportionate human anatomy. Facial expressions should be calm, poised, and graceful, matching the luxurious vibe of a high-end fashion studio shoot. Ensure that a real, realistic human model is depicted wearing the lehenga in the video. No AI distortions, unreal or doll-like figures, or unrealistic body features should appear in any shot. This video should deliver a hyper-realistic, immersive, and elegant look, capturing the true craftsmanship of the lehenga collection while keeping all visuals natural and authentic.",
      category: "lehenga",
      garmentType: "lehenga",
      tags: ["lehenga", "indian", "hyper-realistic", "cinematic", "studio", "authentic", "slow-motion", "craftsmanship"],
      illustrativeImage: "https://i.ibb.co/example/lehenga-authentic-studio.jpg",
      videoSample: "/pose3.mp4"
    },
    {
      id: "saree_001",
      name: "Saree Royal Courtyard",
      description: "Graceful Indian woman in a sunlit royal courtyard wearing a beautifully draped saree with traditional jewelry",
      prompt: "A graceful Indian woman in her late 20s poses elegantly for a saree photoshoot. She is wearing a beautifully draped saree as shown in refrence image paired with classic gold jewelry and light, natural makeup. Setting: A sunlit royal courtyard of a heritage haveli or palace in Rajasthan, with carved arches and pillars in the background. Warm golden hour lighting casts a soft glow, creating a regal yet intimate atmosphere. Action: The camera captures slow-motion shots as she gracefully adjusts her saree pallu over her shoulder. She gently twirls, allowing the saree's border and pleats to flow. She walks slowly along the veranda, looking back over her shoulder with a soft smile. Close-up shots focus on the intricate fabric details, the drape, and her traditional jewelry. Occasional candid moments — she tucks a strand of hair behind her ear, or looks down shyly and smiles. Style & Mood: Cinematic, warm and elegant. The vibe is timeless — celebrating Indian tradition, femininity, and grace.",
      category: "saree",
      garmentType: "saree",
      tags: ["saree", "indian", "traditional", "royal", "courtyard", "golden-hour", "graceful", "cinematic"],
      illustrativeImage: "https://i.ibb.co/example/saree-royal-courtyard.jpg",
      videoSample: "/pose4.mp4"
    },
    {
      id: "saree_002",
      name: "Saree Runway Walk",
      description: "5-second cinematic fashion video of a beautiful Indian model walking confidently on a runway wearing a red saree",
      prompt: "A 5-second cinematic fashion video of a beautiful Indian model walking confidently on a runway wearing the exact same red saree and blouse shown in the reference images. The saree's pattern, embroidery, and colors must remain identical with no changes. The model walks gracefully toward the camera on a well-lit fashion show ramp, with runway lights overhead and an elegant seated audience blurred in the background. The pleats flow naturally with each step, and the pallu drapes gracefully over her shoulder. ✅ The model's body proportions must remain natural and realistic — no unnatural twists of the neck, arms, or hands. ✅ The camera angle should start low and slightly tilt up to capture the full saree details, pleats, and embroidery as she walks. ✅ Add subtle slow-motion touches to highlight the fabric's flow and intricate embroidery. ✅ Keep the lighting soft and professional, focusing on the richness of the saree's red and gold tones. ✅ The vibe should feel elegant, confident, and high-end, just like a real fashion show.",
      category: "saree",
      garmentType: "saree",
      tags: ["saree", "indian", "runway", "cinematic", "fashion-show", "red-saree", "gold-embroidery", "slow-motion"],
      illustrativeImage: "https://i.ibb.co/example/saree-runway-walk.jpg",
      videoSample: "/pose5.mp4"
    }
  ];

  // Men's Fashion templates
  const mensFashionTemplates = [
    
    {
      id: "kurta_002",
      name: "Kurta Hyper-Realistic Studio",
      description: "Hyper-realistic video shoot of a handsome Indian male model wearing kurta and pajama in an elegant fashion studio",
      prompt: "Create a hyper-realistic image and short video of a handsome Indian male model wearing a kurta and pajama that exactly replicate the reference — including the precise peach-orange color, the white floral and diamond embroidery pattern, the same fabric texture, and the covered buttons as shown in the close-up detail image. There should be no design changes — the pattern, placement, and texture must match perfectly. The pajama should be crisp white, simple, and well-fitted as in the earlier reference. There should be no change in the pattern for the back. Set the scene in an elegant indoor fashion studio with soft, warm ambient lighting to highlight the fine embroidery and fabric texture. Use a clean, neutral-colored backdrop (like light beige or soft grey) so the outfit stands out. For the photo: The model should stand straight or slightly angled, with a confident yet relaxed pose, gentle smile, and natural posture. Hands can rest in the pajama pocket or lightly hold a side pleat of the kurta. For the video: The model should slowly walk toward the camera for a few seconds, then gently turn to show a side angle, naturally showing the embroidery detail from different angles. The video should be high-resolution, smooth, and editorial in style, ideal for an upscale ethnic menswear catalog or reel. Focus on keeping the kurta's embroidery and buttons crystal clear, realistic, and exactly like the reference close-up image.",
      category: "kurta",
      garmentType: "kurta",
      tags: ["kurta", "pajama", "hyper-realistic", "studio", "peach-orange", "embroidery", "traditional", "elegant"],
      illustrativeImage: "https://i.ibb.co/example/kurta-hyper-realistic.jpg",
      videoSample: "/pose6.mp4"
    },
    
  ];

  const getCurrentTemplates = () => {
    if (activeTab === 'women-fashion') {
      return womenFashionTemplates.filter(template => 
        selectedGarmentType === 'all' || template.garmentType === selectedGarmentType
      );
    } else if (activeTab === 'mens-fashion') {
      return mensFashionTemplates.filter(template => 
        selectedGarmentType === 'all' || template.garmentType === selectedGarmentType
      );
    }
    return [];
  };

  const getCurrentGarmentTypes = () => {
    return activeTab === 'women-fashion' ? garmentTypes : mensGarmentTypes;
  };

  const filteredTemplates = getCurrentTemplates();

  // Reset selected garment type when switching tabs
  useEffect(() => {
    setSelectedGarmentType('all');
  }, [activeTab]);

  // Add this useEffect after the existing useEffect
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscapeKey, true);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div 
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 max-w-6xl w-full relative max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
          <XCircle size={28}/>
        </button>
        
        <h3 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-6 text-center">Choose a Template</h3>

        {/* Tabs */}
        <div className="mb-6 border-b dark:border-gray-700 flex space-x-6 justify-center">
          {/* New Women Fashion Tab */}
          <button
            className={`py-2 px-6 font-semibold text-base border-b-2 transition-colors ${activeTab === 'women-fashion' ? 'border-indigo-500 text-indigo-700 dark:text-indigo-400' : 'border-transparent text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400'}`}
            onClick={() => setActiveTab('women-fashion')}
          >
            Women Fashion
          </button>
          {/* New Men's Fashion Tab - Disabled */}
          <button
            className="py-2 px-6 font-semibold text-base border-b-2 transition-colors border-transparent text-gray-300 cursor-not-allowed opacity-50"
            disabled
            title="Men's Fashion is currently disabled"
          >
            Men's Fashion
          </button>
        </div>

        {/* Garment Type Filter */}
        <div className="mb-6 flex justify-center">
          <div className="flex flex-wrap gap-2 justify-center">
            {Object.entries(getCurrentGarmentTypes()).map(([key, value]) => (
              <button
                key={key}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedGarmentType === key
                    ? 'bg-indigo-600 text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
                onClick={() => setSelectedGarmentType(key)}
              >
                {value}
              </button>
            ))}
          </div>
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Currently empty for Women Fashion tab */}
          {filteredTemplates.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-500 dark:text-gray-400 mb-4">
                <ImageIcon size={48} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No templates available</h3>
                <p className="text-sm">Women Fashion templates will be added soon.</p>
              </div>
            </div>
          ) : (
            filteredTemplates.map((template) => (
              <div
                key={template.id}
                className={`group border dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 p-0 flex flex-col shadow-sm hover:shadow-xl transition-all cursor-pointer relative overflow-hidden ${selectedTemplate?.id === template.id ? 'border-indigo-500 ring-2 ring-indigo-200 dark:ring-indigo-900' : ''}`}
                onClick={() => onSelect(template)}
                style={{ minHeight: 320 }}
              >
                {/* Full video card */}
                <div className="h-full w-full bg-black flex items-center justify-center overflow-hidden relative">
                  {template.videoSample ? (
                    <div className="relative w-full h-full bg-black">
                      <video 
                        src={template.videoSample}
                        className="object-cover w-full h-full"
                        muted
                        loop
                        autoPlay
                        playsInline
                      />
                      {selectedTemplate?.id === template.id && (
                        <div className="absolute top-2 right-2 bg-white dark:bg-gray-800 rounded-full p-1 shadow">
                          <CheckCircle size={20} className="text-green-500"/>
                        </div>
                      )}
                    </div>
                  ) : (
                    <img 
                      src={template.illustrativeImage || PLACEHOLDER_IMAGES[Math.floor(Math.random() * PLACEHOLDER_IMAGES.length)]}
                      alt={template.name}
                      className="object-cover w-full h-full group-hover:scale-105 transition-transform bg-white dark:bg-gray-800"
                    />
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateSelector; 